import { Container, Sprite } from "pixi.js"
import { TreeArt, fallbackTreeArt } from "./tree_art"
import { TreeDebris, TreeDebrisTextures, between, pick } from "./tree_debris"

/**
 * What a tree does when something hits it.
 *
 * This replaced a 25Hz sideways jitter of the whole sprite, 3-6px for ~150ms. Three things were wrong
 * with that, and each of the parts below answers one of them:
 *
 *   THE GROUND MOVED. Every pixel of the tree slid by the same amount, roots and shadow included, so
 *   the tree read as a decal being nudged rather than as something rooted being struck. The RECOIL
 *   here is a shear about the foot of the art -- the roots stay planted and the crown carries the whole
 *   swing -- using the same "skew about the top, slide back by the base's travel" pair GrassSway uses,
 *   so a tree split across several sprites still bends as one piece.
 *
 *   IT WAS A VIBRATION, NOT A BLOW. 25Hz squeezed into nine frames aliases into a buzz. The recoil is a
 *   damped spring kicked by an impulse AWAY from whoever struck it: the crown lurches over inside the
 *   first ~80ms, swings back past upright and settles over the better part of a second, about three
 *   tenths of a second a swing. Blows that land while it is still swinging add to the motion they
 *   land on, so a run of axe strokes works the tree rather than restarting a canned animation.
 *
 *   NOTHING CAME OFF THE TREE. A hit now knocks CHIPS out of the trunk where the axe bit, toward the
 *   swing -- pale fresh wood and the tree's own bark, arcing out, bouncing once and lying in the dirt --
 *   with a small puff of bark dust, and shakes LEAVES loose from the canopy that flutter down in front
 *   of it. Their colours are read off the tree's own art (tree_art.ts), so a blossom tree drops petals.
 *
 * Everything scales with how hard the blow was: an axe stroke is 1, a fist is a third of that, a
 * warhammer half again.
 *
 * NO TIMERS. One update() off the render loop advances every recoil and every particle (the old shake
 * held a setInterval per struck tile). The particles are ordinary z-sorted children of the world
 * surface, so they interleave with bodies the way FootDust's puffs do, and never sort behind the tree
 * they came off.
 *
 * LIVE TUNING, from the browser console: window.treeHit.{enabled, lean, frequency, damping, leaves, chips}
 */

export type TreeHitTextures = TreeDebrisTextures

export interface TreeStrike {
    // Identifies the tree across frames; a second blow on the same key adds to the swing in progress.
    key: string
    // The tree's CURRENT sprites. Re-read every frame rather than held: a full map rerender hands the
    // tree new pooled sprites mid-swing, and the old ones may by then be some other tile.
    sprites: () => Sprite[] | undefined
    // What the art looks like (where the trunk is, its colours). Undefined: a generic tree shape.
    art?: TreeArt
    // World position of whoever struck it. Undefined: a random side.
    from?: { x: number; y: number }
    // 1 = an axe blow.
    strength: number
    // Carry a passive wind bend into the first recoil instead of snapping upright on contact.
    initialLean?: number
}

export interface TreeHitTuning {
    enabled: boolean
    // Peak lean of an axe blow: px of sway per px of height above the roots. 0.032 puts the top of a
    // five-tile pine about five pixels over.
    lean: number
    // The sway's natural frequency in Hz, and its damping ratio. ~3Hz and 0.2 is about three visible
    // swings, mostly settled by 0.6s: springy, but done before the next stroke of a steady chop lands.
    frequency: number
    damping: number
    // Leaves shaken loose, and chips knocked out, by one axe blow.
    leaves: number
    chips: number
}

interface Base {
    // Where the map put the sprite, and what this effect last wrote over it. A sprite whose x is no
    // longer what was written has been repositioned by the map (a rerender) and is re-based.
    x: number
    written: number
}

interface Recoil {
    footOffset: number
    sprites: () => Sprite[] | undefined
    // The lean, as px of horizontal travel per px of height (positive = top to the right), and its rate.
    angle: number
    velocity: number
    bases: Map<Sprite, Base>
}

// Integration step for the spring: semi-implicit Euler is comfortably stable at 4ms for a 3Hz spring,
// and a long frame is taken in several of these rather than one big step.
const STEP = 1 / 240
// A frame longer than this is a stall, not motion.
const MAX_FRAME_SECONDS = 0.05
// Hard limit on the lean, however many blows stack up: about four degrees.
const MAX_LEAN = 0.075
// Below both of these the tree is upright and still, and is handed back to the map untouched.
const REST_ANGLE = 0.0004
const REST_VELOCITY = 0.01

const CHIP_SPEED_MIN = 35
const CHIP_SPEED_MAX = 95
const PUFF_LIFE_MS = 420

// What the inside of a tree looks like: most chips are this, not bark. A chip of bark alone reads as
// dirt; the pale ones are what say "wood".
export const FRESH_WOOD = [0xe7cb98, 0xd8b37c, 0xc99c63]
export const BARK_DUST = 0xd6c2a0

const alive = (s: Sprite | undefined): s is Sprite => !!s && !s.destroyed && !!s.transform && !!s.parent
const spriteHeight = (s: Sprite) => s.texture.height * Math.abs(s.scale.y)

// Peak lean reached by a spring at rest kicked with unit velocity: the impulse is sized from this, so
// `lean` means the peak whatever the frequency and damping are tuned to.
const peakPerUnitVelocity = (omega: number, zeta: number) => {
    const damped = omega * Math.sqrt(Math.max(0.01, 1 - zeta * zeta))
    const tPeak = Math.atan2(damped, zeta * omega) / damped
    return (Math.exp(-zeta * omega * tPeak) * Math.sin(damped * tPeak)) / damped
}

export class TreeHit {
    readonly tuning: TreeHitTuning = {
        enabled: true,
        lean: 0.032,
        frequency: 3.1,
        damping: 0.2,
        leaves: 5,
        chips: 5,
    }

    // What comes off the tree. Public so the fall that ends a tree (TreeFall) sheds into the same pool.
    readonly debris: TreeDebris
    private recoils = new Map<string, Recoil>()
    private lastMs?: number

    setSurface(surface: Container): void {
        this.debris.setSurface(surface)
    }

    constructor(surface: Container, makeTextures: () => TreeHitTextures) {
        this.debris = new TreeDebris(surface, makeTextures)
        if (typeof window !== "undefined") {
            ;(window as any).treeHit = this.tuning
        }
    }

    // Exposed for tests.
    isSwaying = (key: string) => this.recoils.has(key)
    leanOf = (key: string) => this.recoils.get(key)?.angle ?? 0
    particleCount = () => this.debris.count()

    /**
     * Let go of a tree that is coming down: hand its sprites back upright and say how it was swinging,
     * so the fall can carry on from the lean the last blow put it in rather than snapping upright first.
     */
    release = (key: string): { angle: number; velocity: number } | undefined => {
        const recoil = this.recoils.get(key)
        if (!recoil) return undefined
        this.straighten(recoil, (recoil.sprites() || []).filter(alive))
        this.recoils.delete(key)
        return { angle: recoil.angle, velocity: recoil.velocity }
    }

    /** Hit a tree. Returns false when there was nothing on screen to hit. */
    strike = (strike: TreeStrike, nowMs: number = performance.now()): boolean => {
        if (!this.tuning.enabled) return false
        const set = (strike.sprites() || []).filter(alive)
        if (!set.length) return false

        let recoil = this.recoils.get(strike.key)
        if (!recoil) {
            recoil = { footOffset: 0, sprites: strike.sprites, angle: strike.initialLean ?? 0, velocity: 0, bases: new Map() }
            this.recoils.set(strike.key, recoil)
        }
        recoil.sprites = strike.sprites

        // Where the art is in the world, measured from where the map PUT the sprites, not where the
        // swing has them this frame.
        let left = Infinity
        let top = Infinity
        let right = -Infinity
        let bottom = -Infinity
        let treeZ = -Infinity
        for (const s of set) {
            const x = this.baseX(recoil, s)
            left = Math.min(left, x)
            top = Math.min(top, s.y)
            right = Math.max(right, x + s.texture.width * Math.abs(s.scale.x))
            bottom = Math.max(bottom, s.y + spriteHeight(s))
            treeZ = Math.max(treeZ, s.zIndex)
        }
        const width = right - left
        const height = bottom - top
        // The analysed art describes the whole composite; a set that is not that size is not that art.
        const art = strike.art && Math.abs(strike.art.width - width) <= 1 && Math.abs(strike.art.height - height) <= 1
            ? strike.art
            : fallbackTreeArt(width, height)

        recoil.footOffset = art === strike.art ? height - art.footY : 0

        const trunkX = left + (art.trunkLeft + art.trunkRight + 1) / 2
        const dx = strike.from ? trunkX - strike.from.x : 0
        // Knocked AWAY from the blow. Struck from dead ahead (or by nothing in particular), either way.
        const away = Math.abs(dx) >= 4 ? Math.sign(dx) : Math.random() < 0.5 ? -1 : 1
        const strength = Math.max(0.2, Math.min(2, strike.strength || 1))

        const omega = 2 * Math.PI * this.tuning.frequency
        recoil.velocity += (away * strength * this.tuning.lean) / peakPerUnitVelocity(omega, this.tuning.damping)

        const scene = {
            left,
            top,
            pivot: bottom,
            footY: top + art.footY,
            trunkX,
            halfTrunk: (art.trunkRight - art.trunkLeft + 1) / 2,
            impactHeight: Math.max(4, art.footY - art.impactY),
            zFloor: treeZ + 0.5,
            lean: recoil.angle,
        }
        // Chips fly back toward the swing; from dead ahead they spray both ways.
        this.spawnChips(scene, art, Math.abs(dx) >= 6 ? -away : 0, strength, nowMs)
        this.spawnLeaves(scene, art, away, strength, nowMs)
        return true
    }

    /** Advance every swing and every particle. Called once per drawn frame. */
    update = (nowMs: number = performance.now()) => {
        if (!this.tuning.enabled) { this.clear(); return }
        if (this.lastMs === undefined) {
            this.lastMs = nowMs
            return
        }
        const dt = Math.min(Math.max(0, (nowMs - this.lastMs) / 1000), MAX_FRAME_SECONDS)
        this.lastMs = nowMs
        if (this.recoils.size) this.advanceRecoils(dt)
        this.debris.update(nowMs, dt)
    }

    /** Stand every tree up and sweep the particles away (a new room). */
    clear = () => {
        this.recoils.forEach(recoil => this.straighten(recoil, (recoil.sprites() || []).filter(alive)))
        this.recoils.clear()
        this.debris.clear()
        this.lastMs = undefined
    }

    releaseSprites = () => {
        this.recoils.forEach(recoil => {
            this.straighten(recoil, (recoil.sprites() || []).filter(alive))
            recoil.bases.clear()
        })
    }

    // ---------------------------------------------------------------------------------------------
    // The swing
    // ---------------------------------------------------------------------------------------------

    private advanceRecoils = (dt: number) => {
        const omega = 2 * Math.PI * this.tuning.frequency
        const zeta = this.tuning.damping
        this.recoils.forEach((recoil, key) => {
            for (let left = dt; left > 1e-6; left -= STEP) {
                const h = Math.min(STEP, left)
                recoil.velocity += (-omega * omega * recoil.angle - 2 * zeta * omega * recoil.velocity) * h
                recoil.angle += recoil.velocity * h
            }
            recoil.angle = Math.max(-MAX_LEAN, Math.min(MAX_LEAN, recoil.angle))

            const set = (recoil.sprites() || []).filter(alive)
            if (!set.length) {
                // Felled, or the room went away. Nothing of ours is left on screen to put back.
                this.recoils.delete(key)
                return
            }
            if (Math.abs(recoil.angle) < REST_ANGLE && Math.abs(recoil.velocity) < REST_VELOCITY) {
                this.straighten(recoil, set)
                this.recoils.delete(key)
                return
            }
            const current = new Set(set)
            recoil.bases.forEach((_, sprite) => { if (!current.has(sprite)) recoil.bases.delete(sprite) })
            this.lean(recoil, set)
        })
    }

    // Shear every piece of the tree about the foot of the art. Each piece's top and bottom edge land
    // where the lean puts that height, so the pieces of a split tree meet exactly; for the usual single
    // sprite this is just a lean with the roots pinned. asin rather than the small-angle shortcut for
    // the same reason GrassSway gives: at the world's 2x zoom a fraction of a pixel shows as a seam.
    private lean = (recoil: Recoil, set: Sprite[]) => {
        let pivot = -Infinity
        for (const s of set) pivot = Math.max(pivot, s.y + spriteHeight(s))
        pivot -= recoil.footOffset
        for (const s of set) {
            const h = spriteHeight(s)
            if (h <= 0) continue
            let base = recoil.bases.get(s)
            if (!base || s.x !== base.written) {
                base = { x: s.x, written: s.x }
                recoil.bases.set(s, base)
            }
            const top = recoil.angle * (pivot - s.y)
            const bottom = recoil.angle * (pivot - s.y - h)
            s.skew.x = Math.asin(Math.max(-1, Math.min(1, (bottom - top) / h)))
            s.x = base.x + top
            base.written = s.x
        }
    }

    // Hand the sprites back exactly as the map left them -- but only the ones still ours: a sprite whose
    // x is not what this effect last wrote has been re-placed by the map and is not touched.
    private straighten = (recoil: Recoil, set: Sprite[]) => {
        for (const s of set) {
            const base = recoil.bases.get(s)
            if (base && s.x === base.written) {
                s.x = base.x
                s.skew.x = 0
            }
        }
    }

    private baseX = (recoil: Recoil, s: Sprite) => {
        const base = recoil.bases.get(s)
        return base && s.x === base.written ? base.x : s.x
    }

    // ---------------------------------------------------------------------------------------------
    // What comes off the tree
    // ---------------------------------------------------------------------------------------------

    private spawnChips = (scene: Scene, art: TreeArt, toward: number, strength: number, nowMs: number) => {
        const count = Math.round(this.tuning.chips * Math.min(strength, 1.5) * between(0.8, 1.2))
        const bark = art.barkColors.length ? art.barkColors : FRESH_WOOD
        // Where the axe bit, on the face toward the swing, carried by the lean at that height.
        const lift = scene.lean * scene.impactHeight
        for (let i = 0; i < count; i++) {
            const side = toward || (Math.random() < 0.5 ? -1 : 1)
            const speed = between(CHIP_SPEED_MIN, CHIP_SPEED_MAX) * (0.7 + 0.3 * Math.min(strength, 1.5))
            const tint = Math.random() < 0.6 ? pick(FRESH_WOOD) : pick(bark)
            if (!this.debris.chip({
                x: scene.trunkX + side * Math.max(1, scene.halfTrunk - 1) + lift,
                groundY: scene.footY + between(0, 3),
                z: scene.impactHeight,
                vx: side * speed,
                // Struck from dead ahead, chips come out toward the viewer as much as sideways.
                vy: toward ? between(-10, 26) : between(10, 40),
                vz: between(45, 110),
                zFloor: scene.zFloor,
            }, tint, nowMs)) return
        }

        // A breath of bark dust off the cut, on the side the axe came from.
        if (count > 0) {
            const side = toward || (Math.random() < 0.5 ? -1 : 1)
            for (let i = 0; i < (strength >= 1 ? 2 : 1); i++) {
                if (!this.debris.puff({
                    x: scene.trunkX + side * scene.halfTrunk + lift,
                    groundY: scene.footY + 1,
                    z: scene.impactHeight - 2,
                    vx: side * between(10, 26),
                    vz: between(6, 14),
                    zFloor: scene.zFloor,
                }, BARK_DUST, nowMs, {
                    scale: between(0.26, 0.38),
                    alpha: between(0.4, 0.6),
                    lifeMs: PUFF_LIFE_MS * between(0.8, 1.2),
                    growth: between(1.6, 2.1),
                })) return
            }
        }
    }

    private spawnLeaves = (scene: Scene, art: TreeArt, away: number, strength: number, nowMs: number) => {
        const points = art.canopyPoints
        if (points.length < 2) return
        const colors = art.canopyColors.length ? art.canopyColors : [0x548c3a]
        const count = Math.max(0, Math.round(this.tuning.leaves * Math.min(strength, 1.5) * between(0.7, 1.3)))
        for (let i = 0; i < count; i++) {
            const p = 2 * Math.floor(Math.random() * (points.length / 2))
            const worldY = scene.top + points[p + 1]
            // Shaken off where the canopy IS this frame, mid-lean.
            const worldX = scene.left + points[p] + scene.lean * (scene.pivot - worldY)
            // Leaves land around the foot of the tree, in front of it: the canopy overhangs the ground
            // there as much as anywhere, and a leaf landing behind the trunk would be hidden by it.
            const groundY = scene.footY + between(-2, 12)
            if (!this.debris.leaf({
                x: worldX,
                groundY,
                z: Math.max(6, groundY - worldY),
                // Flung the way the crown lurched, then the air takes over.
                vx: away * between(10, 32) * Math.min(strength, 1.5) + between(-6, 6),
                vy: between(-2, 6),
                vz: between(0, 18),
                zFloor: scene.zFloor,
            }, pick(colors), nowMs)) return
        }
    }
}

interface Scene {
    // Top-left of the art in the world, and the line it pivots on (the bottom of the art).
    left: number
    top: number
    pivot: number
    // Where the roots meet the ground, the trunk's middle, and how high on it the axe landed.
    footY: number
    trunkX: number
    halfTrunk: number
    impactHeight: number
    zFloor: number
    // The tree's lean at the moment of the blow.
    lean: number
}
